import { computed, ref } from 'vue'
import { useAuth } from './useAuth'
import {
  createBankingAccountDetail,
  getBankingAccountDetail,
  updateBankingAccountDetail,
} from '@/services/account'

/**
 * The member's refund bank details (RIB).
 *
 * A single RIB — the first `refund` banking account detail found across the
 * member's contracts. The list exposes the masked IBAN/BIC; the full IBAN and
 * BIC are fetched on demand (reveal) and never kept longer than needed. Editing
 * goes through `PATCH …/banking_account_detail/:id`, then reloads the contracts
 * so the masked values reflect the change. The edit form is never pre-filled
 * (the full IBAN/BIC must be re-entered), so no full detail is fetched for it.
 */
export type MaskedBank = {
  contractId: string
  bankingId: string
  holder: string
  maskedIban: string
  maskedBic: string
  bankName: string | null
}

export function useBank() {
  const { rawContracts, reloadContracts } = useAuth()

  /** RIB unique : 1er `banking_account_detail` de type `refund` sur les contrats. */
  const bank = computed<MaskedBank | null>(() => {
    for (const contract of rawContracts.value) {
      const detail = contract.banking_account_details?.find((b) => b.kind === 'refund')
      if (detail) {
        return {
          contractId: contract.id,
          bankingId: detail.id,
          holder: detail.owner_fullname,
          maskedIban: detail.masked_iban,
          maskedBic: detail.masked_bic,
          bankName: detail.bank_name ?? null,
        }
      }
    }
    return null
  })

  // Révélation de l'IBAN et du BIC complets (chargés à la demande, une seule fois).
  const fullIban = ref<string | null>(null)
  const fullBic = ref<string | null>(null)
  const revealing = ref(false)
  const revealError = ref(false)

  /** Récupère l'IBAN + le BIC complets via le GET détaillé. Idempotent. */
  async function reveal(): Promise<void> {
    if (!bank.value || (fullIban.value && fullBic.value) || revealing.value) return
    revealing.value = true
    revealError.value = false
    try {
      const detail = await getBankingAccountDetail(bank.value.contractId, bank.value.bankingId)
      fullIban.value = detail.iban
      fullBic.value = detail.bic
    } catch {
      revealError.value = true
    } finally {
      revealing.value = false
    }
  }

  /** Met à jour le RIB de remboursement, puis recharge les contrats (valeurs masquées). */
  async function saveBank(values: {
    iban: string
    bic: string
    ownerFullname: string
    bankName?: string
  }): Promise<void> {
    if (!bank.value) return
    await updateBankingAccountDetail(bank.value.contractId, bank.value.bankingId, values)
    // Les valeurs précédemment révélées ne sont plus à jour.
    fullIban.value = null
    fullBic.value = null
    await reloadContracts()
  }

  /** Crée le premier RIB de remboursement sur le contrat donné, puis recharge
   *  les contrats pour que le RIB (valeurs masquées) apparaisse. */
  async function createBank(
    contractId: string,
    values: { iban: string; bic: string; ownerFullname: string; bankName?: string },
  ): Promise<void> {
    await createBankingAccountDetail(contractId, values)
    fullIban.value = null
    fullBic.value = null
    await reloadContracts()
  }

  return { bank, fullIban, fullBic, revealing, revealError, reveal, saveBank, createBank }
}
